import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FREE_RUNS_PER_DAY, canStartRun, registerRun, runsLeftToday } from '../../lib/scaleLimit';
import { usePremium } from '../../lib/session';
import { useUI } from '../../lib/ui';
import 'shepherd.js/dist/css/shepherd.css';

import GemDrops from '../../components/Idle/GemDrops';
import Instrument from '../../components/Instrument/Instrument';
import Options from '../../components/Options/Options';
import ScaleNotes from '../../components/ScaleNotes/ScaleNotes';
import { awardScaleGem, completeScaleSession } from '../../lib/idle/idleStore';
import { keyToValve, normalizeKey } from '../../lib/keyBindings';
import { brassDict } from '../../utils/Dictionaries/brassDict';
import { scalesDict } from '../../utils/Dictionaries/scalesDict';
import {
  concertTonicName,
  displayedTonic,
  readTranspositionSettings,
  scaleToDisplayed,
  writtenToSampleNote,
} from '../../utils/transposition';
import { createClicker } from './clicker';
import Styles from './Scales.module.css';
import {
  MAX_COUNT_IN,
  bestKey,
  buildEmptyStats,
  concertTonic,
  fingeringLabels,
  getKeyChoices,
  getOctaveChoices,
  loadBestBpms,
  matchesFingering,
  parseScaleId,
  resolveScaleId,
  saveBestBpms,
} from './scaleTools';

const EMPTY_SCALE = [];
const NO_STATUSES = [];
const noop = () => {};

/* ---------- saved settings ---------- */

const readOptions = () => {
  try {
    return JSON.parse(localStorage.getItem('options')) || {};
  } catch {
    return {};
  }
};

const loadOptions = () => ({
  selectedInstrument: 'trumpet',
  volume: 50,
  scaleKey: 'C',
  scaleOctave: 4,
  ...readOptions(),
});

// Merged into the same 'options' entry the Options modal uses, so the two
// stay in sync.
const saveOptions = (patch) => {
  try {
    localStorage.setItem('options', JSON.stringify({ ...readOptions(), ...patch }));
  } catch {
    // storage unavailable; the choice just won't persist
  }
};

const METRONOME_KEY = 'brassflows_scales_metronome_v1';
const METRONOME_DEFAULTS = { bpm: 120, countIn: 4, clickVolume: 50 };

const loadMetronome = () => {
  try {
    return { ...METRONOME_DEFAULTS, ...JSON.parse(localStorage.getItem(METRONOME_KEY)) };
  } catch {
    return METRONOME_DEFAULTS;
  }
};

const formatTime = (timeInMs) => {
  const minutes = String(Math.floor((timeInMs / 60000) % 60)).padStart(2, '0');
  const seconds = String(Math.floor((timeInMs / 1000) % 60)).padStart(2, '0');
  const hundredths = String(Math.floor((timeInMs % 1000) / 10)).padStart(2, '0');
  return `${minutes}:${seconds}:${hundredths}`;
};

const playNote = (instrument, note, volume) => {
  try {
    const sound = new Audio(`/sounds/${instrument}/${note.replace('#', 's')}.mp3`);
    sound.volume = Math.max(0, Math.min(1, volume / 100));
    const playing = sound.play();
    if (playing && playing.catch) playing.catch(() => {});
  } catch {
    // a missing or blocked sound shouldn't stop practice
  }
};

/* ---------- small pieces ---------- */

const Chevron = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
    <path
      d="M2.5 4.5L6 8l3.5-3.5"
      stroke="currentColor"
      strokeWidth="1.8"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function Dropdown({ items, selected, onSelect, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={Styles.dropdown} ref={ref}>
      <button
        type="button"
        className={Styles.pill}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {children}
        <Chevron />
      </button>
      {open && (
        <ul className={Styles.menu} role="listbox">
          {items.map((item) => (
            <li key={item.value} role="option" aria-selected={item.value === selected}>
              <button
                type="button"
                className={`${Styles.menuItem} ${
                  item.value === selected ? Styles.menuItemSelected : ''
                }`}
                onClick={() => {
                  setOpen(false);
                  onSelect(item.value);
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- page ---------- */

const Scales = () => {
  const navigate = useNavigate();
  const premium = usePremium();
  const openPricing = useUI((s) => s.openPricing);
  const [runsLeft, setRunsLeft] = useState(runsLeftToday);

  const [isReady, setIsReady] = useState(false); // fades in once the valve images are loaded
  const [showOptions, setShowOptions] = useState(false);

  const [initial] = useState(loadOptions);
  const [selectedInstrument, setSelectedInstrument] = useState(initial.selectedInstrument);
  const [volume, setVolume] = useState(initial.volume);
  const [scaleKey, setScaleKey] = useState(initial.scaleKey || 'C');
  const [scaleOctave, setScaleOctave] = useState(initial.scaleOctave || 4);
  // Trumpet key and notation (written / concert) from Options
  const [pitchChoice, setPitchChoice] = useState(() =>
    readTranspositionSettings(initial, { page: 'scales' }),
  );

  const [metronome, setMetronome] = useState(loadMetronome);
  const { bpm, countIn } = metronome;

  // idle -> (space) -> countin -> playing -> idle. Handlers read the ref so
  // they never act on an old phase.
  const [phase, setPhaseState] = useState('idle');
  const phaseRef = useRef('idle');
  const setPhase = useCallback((next) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);
  const [countBeat, setCountBeat] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Per-note results of the current run: null (not yet), 'right' or 'wrong'.
  const [statuses, setStatuses] = useState(NO_STATUSES);
  const statusesRef = useRef([]);
  const [lastRun, setLastRun] = useState(null);

  const [notesRight, setNotesRight] = useState(0);
  const [notesWrong, setNotesWrong] = useState(0);
  const [perfectRuns, setPerfectRuns] = useState(0);
  const [gemsEarned, setGemsEarned] = useState(0);
  const [stats, setStats] = useState(buildEmptyStats);
  const [bestBpms, setBestBpms] = useState(loadBestBpms);
  const shortestRunRef = useRef(null);

  const [feedbackType, setFeedbackType] = useState(null);
  const feedbackTimerRef = useRef(null);

  const heldRef = useRef([]); // valve keys held right now, reported by the Instrument
  const runRef = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const clickerRef = useRef(null);
  if (!clickerRef.current) clickerRef.current = createClicker();

  /* Which scale is on screen */
  const scaleId = resolveScaleId(selectedInstrument, scaleKey, scaleOctave);
  const currentScale =
    (scaleId && scalesDict[selectedInstrument]?.[scaleId]) || EMPTY_SCALE;
  const parsed = scaleId ? parseScaleId(scaleId) : null;
  const tonic = parsed ? parsed.tonic : scaleKey;
  const octave = parsed ? parsed.octave : scaleOctave;
  // Scales are stored as written pitch and graded as written pitch. Only what is
  // drawn and named follows the notation setting; fingering, stats and best
  // tempos keep using the written scale.
  const pitch = { instrument: selectedInstrument, ...pitchChoice };
  const shownKey = (key) =>
    selectedInstrument === 'trumpet' ? displayedTonic(key, pitch) : key;
  const displayedScale = scaleToDisplayed(currentScale, pitch);
  let keyHint = null;
  if (selectedInstrument === 'trumpet') {
    const inConcert = pitch.notation === 'concert';
    const other = inConcert ? tonic : concertTonicName(tonic, pitch.trumpetKey);
    if (other !== shownKey(tonic)) keyHint = `${inConcert ? 'Written' : 'Concert'} ${other} Major`;
  } else {
    const concert = concertTonic(selectedInstrument, tonic);
    if (concert) keyHint = `Concert ${concert} Major`;
  }
  const bestBpm = scaleId ? bestBpms[bestKey(selectedInstrument, scaleId)] : undefined;

  const keyItems = getKeyChoices(selectedInstrument).map((key) => ({
    value: key,
    label: `${shownKey(key)} Major`,
  }));
  const octaveItems = getOctaveChoices(selectedInstrument, tonic).map((value) => ({
    value,
    label: scaleToDisplayed([`${tonic}${value}`], pitch)[0],
  }));

  // Latest values for code that outlives a render (the beat loop, key listener).
  const live = useRef({});
  live.current = {
    scale: currentScale,
    instrument: selectedInstrument,
    volume,
    scaleId,
    metronome,
    showOptions,
    pitch,
  };

  /* ---------- feedback flash ---------- */

  const showFeedback = useCallback((type, duration = 300) => {
    setFeedbackType(type);
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = setTimeout(() => {
      setFeedbackType(null);
      feedbackTimerRef.current = null;
    }, duration);
  }, []);

  /* ---------- the beat-driven run ---------- */

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const stopLoop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    runRef.current = null;
  }, []);

  const stopToIdle = useCallback(() => {
    clearTimer();
    stopLoop();
    statusesRef.current = [];
    setStatuses(NO_STATUSES);
    setCountBeat(null);
    setActiveIndex(0);
    setLastRun(null);
    setPhase('idle');
  }, [clearTimer, stopLoop, setPhase]);

  const setNoteStatus = useCallback((index, status) => {
    statusesRef.current[index] = status;
    setStatuses(statusesRef.current.slice());
    const note = live.current.scale[index];
    setStats((prev) => {
      const [right, wrong] = prev[note] || [0, 0];
      return { ...prev, [note]: status === 'right' ? [right + 1, wrong] : [right, wrong + 1] };
    });
    if (status === 'right') setNotesRight((count) => count + 1);
    else setNotesWrong((count) => count + 1);
  }, []);

  const endRun = useCallback(() => {
    const run = runRef.current;
    stopLoop();
    if (!run) return;

    const right = run.results.filter((result) => result === 'right').length;
    const perfect = right === run.total;
    setLastRun({ right, total: run.total, perfect, bpm: run.bpm });

    const runLength = run.total * run.interval;
    if (shortestRunRef.current === null || runLength < shortestRunRef.current) {
      shortestRunRef.current = runLength;
    }

    if (perfect) {
      setPerfectRuns((count) => count + 1);
      // The whole scale right in one run is worth a gem.
      const earned = awardScaleGem(1);
      setGemsEarned((count) => count + (Number(earned) || 0));
      showFeedback('correct', 600);
      const { instrument, scaleId: id } = live.current;
      const key = bestKey(instrument, id);
      setBestBpms((prev) => {
        if (prev[key] !== undefined && prev[key] >= run.bpm) return prev;
        const next = { ...prev, [key]: run.bpm };
        saveBestBpms(next);
        return next;
      });
    }

    setActiveIndex(0);
    setPhase('idle');
  }, [setPhase, showFeedback, stopLoop]);

  // One pass per animation frame. Note i owns the beat window
  // [i * beat, (i + 1) * beat). The note counts as played once the right
  // fingering has been held for a short while inside its window (the last
  // fifth of the previous window counts too, so you can change fingering a
  // touch early). A window that ends without that marks the note wrong.
  const tick = useCallback(() => {
    const run = runRef.current;
    if (!run || phaseRef.current !== 'playing') return;

    const { scale, instrument, volume: level, metronome: settings, pitch: pitchNow } = live.current;
    const now = performance.now();
    const dt = Math.min(64, now - run.lastTs);
    run.lastTs = now;

    const elapsed = Math.max(0, now - run.t0);
    const index = Math.min(run.total - 1, Math.floor(elapsed / run.interval));
    const held = heldRef.current;
    const needMs = Math.max(50, Math.min(200, run.interval * 0.3));

    const credit = (i) => {
      if (i < 0 || i >= run.total || run.results[i]) return;
      if (!matchesFingering(instrument, held, brassDict[instrument]?.[scale[i]])) return;
      run.matched[i] += dt;
      if (run.matched[i] >= needMs) {
        run.results[i] = 'right';
        setNoteStatus(i, 'right');
        playNote(instrument, writtenToSampleNote(scale[i], pitchNow), level);
      }
    };
    credit(index);
    if (elapsed - index * run.interval > run.interval * 0.8) credit(index + 1);

    if (index !== run.lastIndex) {
      run.lastIndex = index;
      setActiveIndex(index);
      clickerRef.current.click(settings.clickVolume / 100, false);
    }

    while (run.done < run.total && elapsed >= (run.done + 1) * run.interval) {
      if (!run.results[run.done]) {
        run.results[run.done] = 'wrong';
        setNoteStatus(run.done, 'wrong');
      }
      run.done += 1;
    }

    if (run.done >= run.total) {
      endRun();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [endRun, setNoteStatus]);

  const startRun = useCallback(() => {
    const { scale, metronome: settings } = live.current;
    const total = scale.length;
    if (total === 0) {
      setPhase('idle');
      return;
    }
    registerRun(); // one of today's free runs (Premium is never counted)
    setRunsLeft(runsLeftToday());

    const now = performance.now();
    runRef.current = {
      t0: now,
      lastTs: now,
      interval: 60000 / settings.bpm,
      bpm: settings.bpm,
      total,
      matched: new Array(total).fill(0),
      results: new Array(total).fill(null),
      done: 0,
      lastIndex: 0,
    };
    statusesRef.current = new Array(total).fill(null);
    setStatuses(statusesRef.current.slice());
    setLastRun(null);
    setActiveIndex(0);
    setCountBeat(null);
    setPhase('playing');
    clickerRef.current.click(settings.clickVolume / 100, true);
    rafRef.current = requestAnimationFrame(tick);
  }, [setPhase, tick]);

  // Spacebar: count the player in (5, 6, 7, 8 by default), then the bar starts
  // moving on the beat.
  const startCountIn = useCallback(() => {
    clearTimer();
    stopLoop();
    statusesRef.current = [];
    setStatuses(NO_STATUSES);
    setLastRun(null);
    setActiveIndex(0);

    const settings = live.current.metronome;
    const beats = Math.max(0, Math.min(MAX_COUNT_IN, Math.round(settings.countIn)));
    if (beats === 0) {
      startRun();
      return;
    }

    const interval = 60000 / settings.bpm;
    const startedAt = performance.now();
    const clickLevel = settings.clickVolume / 100;
    let beat = 0;
    setPhase('countin');

    const step = () => {
      if (beat >= beats) {
        // One beat after the last count number is the downbeat: go.
        startRun();
        return;
      }
      // The count always ends on 8, so 4 beats reads 5-6-7-8 and 2 reads 7-8.
      setCountBeat(8 - beats + 1 + beat);
      clickerRef.current.click(clickLevel, beat === 0);
      beat += 1;
      timerRef.current = setTimeout(
        step,
        Math.max(0, startedAt + beat * interval - performance.now()),
      );
    };
    step();
  }, [clearTimer, setPhase, startRun, stopLoop]);

  // Space starts the count-in. The Instrument is in passive mode, so keys held
  // during the count-in simply show on the valves (set your first fingering
  // while you count) and nothing is graded until the bar starts moving.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (live.current.showOptions) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      const tag = event.target && event.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (event.key === 'Escape') {
        if (phaseRef.current !== 'idle') {
          event.preventDefault();
          stopToIdle();
        }
        return;
      }

      if (event.key === ' ' || event.code === 'Space') {
        event.preventDefault(); // never scroll the page
        if (phaseRef.current === 'idle') {
          if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
          }
          if (!event.repeat) {
            setRunsLeft(runsLeftToday()); // picks up a new day if the page was left open overnight
            if (canStartRun()) startCountIn();
          }
        }
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [startCountIn, stopToIdle]);

  // Held valves are tracked right here instead of being read back from the
  // Instrument, so grading works no matter how the Instrument handles input.
  // The Instrument only draws the valves. heldRef holds valve names ('j', 'k',
  // ...), found through the player's key bindings; the euphonium's trigger is
  // not used in Scales.
  useEffect(() => {
    const valveOf = (event) => {
      const valve = keyToValve(live.current.instrument, normalizeKey(event.key));
      return valve === 'f' ? null : valve;
    };
    const onDown = (event) => {
      const valve = valveOf(event);
      if (!valve || event.ctrlKey || event.metaKey || event.altKey) return;
      if (live.current.showOptions) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      const tag = event.target && event.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (!heldRef.current.includes(valve)) heldRef.current = [...heldRef.current, valve];
    };
    const onUp = (event) => {
      const valve = valveOf(event);
      if (!valve) return;
      heldRef.current = heldRef.current.filter((held) => held !== valve);
    };
    const release = () => {
      heldRef.current = [];
    };
    window.addEventListener('keydown', onDown, true);
    window.addEventListener('keyup', onUp, true);
    window.addEventListener('blur', release);
    return () => {
      window.removeEventListener('keydown', onDown, true);
      window.removeEventListener('keyup', onUp, true);
      window.removeEventListener('blur', release);
    };
  }, []);

  // The beat loop can't keep time in a hidden tab, so stop rather than mark
  // everything wrong.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden && phaseRef.current !== 'idle') stopToIdle();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [stopToIdle]);

  useEffect(
    () => () => {
      clearTimer();
      stopLoop();
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
      clickerRef.current.close();
    },
    [clearTimer, stopLoop],
  );

  // The Instrument reports when its images are ready; this is only a backstop
  // so the page can never stay hidden.
  useEffect(() => {
    const id = setTimeout(() => setIsReady(true), 2500);
    return () => clearTimeout(id);
  }, []);

  /* ---------- settings ---------- */

  const selectKey = (nextTonic) => {
    const nextId = resolveScaleId(selectedInstrument, nextTonic, octave);
    const nextParsed = nextId ? parseScaleId(nextId) : null;
    setScaleKey(nextTonic);
    if (nextParsed) setScaleOctave(nextParsed.octave);
    saveOptions({ scaleKey: nextTonic, scaleOctave: nextParsed ? nextParsed.octave : octave });
    stopToIdle();
    if (nextTonic !== tonic) resetSession();
  };

  const selectOctave = (nextOctave) => {
    setScaleOctave(nextOctave);
    saveOptions({ scaleKey: tonic, scaleOctave: nextOctave });
    stopToIdle();
    if (nextOctave !== octave) resetSession();
  };

  const toggleOptions = () => {
    if (showOptions) {
      // Closing the modal: pick up whatever was changed.
      const opts = loadOptions();
      const changedScale =
        opts.selectedInstrument !== selectedInstrument ||
        (opts.scaleKey || 'C') !== scaleKey ||
        (opts.scaleOctave || 4) !== scaleOctave;
      setSelectedInstrument(opts.selectedInstrument);
      setScaleKey(opts.scaleKey || 'C');
      setScaleOctave(opts.scaleOctave || 4);
      setVolume(opts.volume);
      setPitchChoice(readTranspositionSettings(opts, { page: 'scales' }));
      setMetronome(loadMetronome()); // tempo, count-in and click volume live in Options
      if (changedScale) resetSession();
    }
    setShowOptions(!showOptions);
    stopToIdle();
  };

  const resetSession = () => {
    setNotesRight(0);
    setNotesWrong(0);
    setPerfectRuns(0);
    setGemsEarned(0);
    setStats(buildEmptyStats());
    shortestRunRef.current = null;
  };

  const reset = () => {
    stopToIdle();
    resetSession();
  };

  const handleLoading = useCallback((loading) => setIsReady(!loading), []);


  /* ---------- finishing ---------- */

  const totalNotes = notesRight + notesWrong;
  const accuracyPercent = totalNotes === 0 ? 0 : (notesRight / totalNotes) * 100;

  const handleGraphData = async () => {
    try {
      const response = await fetch('https://brassflows.pythonanywhere.com/api/results', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stats }),
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      return data.graph;
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
      return undefined;
    }
  };

  const finish = async () => {
    if (totalNotes === 0) return;
    stopToIdle();

    const graph = await handleGraphData();

    // Gems were paid as each perfect run happened; this just counts the session.
    completeScaleSession();

    localStorage.setItem(
      'localResults',
      JSON.stringify({
        formattedTime: formatTime(shortestRunRef.current ?? 0),
        notes: totalNotes,
        accuracy: accuracyPercent.toFixed(1),
        graph,
        correct: perfectRuns,
        gems: gemsEarned,
      }),
    );
    navigate('/practice/scales/results');
  };

  /* ---------- render ---------- */

  const fingerings = statuses.map((status, index) =>
    status === 'wrong' ? fingeringLabels(selectedInstrument, currentScale[index]) : null,
  );
  // The counter under the staff is for the run on screen, so it starts over
  // with every new run (Finish still reports the whole session).
  const runRight = statuses.filter((status) => status === 'right').length;
  const runJudged = statuses.filter(Boolean).length;
  const stepMs = Math.max(40, Math.min(120, (60000 / bpm) * 0.35));

  let statusLine;
  if (phase === 'countin') {
    statusLine = 'Get ready. Set your first fingering while you count.';
  } else if (phase === 'playing') {
    statusLine = 'Hold each fingering as the bar reaches its note.';
  } else {
    const prompt = (
      <>
        Press <kbd className={Styles.key}>Space</kbd>{' '}
        {countIn > 0 ? `for a ${countIn}-beat count-in` : 'to start'}
      </>
    );
    if (lastRun && lastRun.perfect) {
      statusLine = <>Perfect run at {lastRun.bpm} BPM! {prompt} to go again.</>;
    } else if (lastRun) {
      statusLine = (
        <>
          {lastRun.right} of {lastRun.total} notes right. {prompt} to try again.
        </>
      );
    } else {
      statusLine = prompt;
    }
  }

  return (
    <main
      className={`${Styles.page} ${isReady ? Styles.pageReady : ''} ${
        showOptions ? Styles.dimmed : ''
      }`}
    >
      <GemDrops />
      <div
        className={`${Styles.feedback} ${
          feedbackType ? `${Styles.show} ${Styles[feedbackType]}` : ''
        }`}
      />

      <div className={Styles.toolbar}>
        <button
          type="button"
          className={`${Styles.optionsBtn} ${showOptions ? Styles.optionsBtnOpen : ''}`}
          onClick={toggleOptions}
          aria-label="Options"
        >
          <svg
            className={Styles.optionsIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="21" x2="14" y1="4" y2="4" />
            <line x1="10" x2="3" y1="4" y2="4" />
            <line x1="21" x2="12" y1="12" y2="12" />
            <line x1="8" x2="3" y1="12" y2="12" />
            <line x1="21" x2="16" y1="20" y2="20" />
            <line x1="12" x2="3" y1="20" y2="20" />
            <line x1="14" x2="14" y1="2" y2="6" />
            <line x1="8" x2="8" y1="10" y2="14" />
            <line x1="16" x2="16" y1="18" y2="22" />
          </svg>
          <span className={Styles.optionsLabel}>Options</span>
        </button>
      </div>

      <div className={Styles.controls}>
        <Dropdown items={octaveItems} selected={octave} onSelect={selectOctave}>
          <span>{scaleToDisplayed([`${tonic}${octave}`], pitch)[0]}</span>
        </Dropdown>

        <div className={Styles.keyBlock}>
          <Dropdown items={keyItems} selected={tonic} onSelect={selectKey}>
            <span className={Styles.keyLabel}>{shownKey(tonic)} Major</span>
            {bestBpm !== undefined && (
              <span className={Styles.best} title="Fastest perfect run">
                ♩ {bestBpm}
              </span>
            )}
          </Dropdown>
          {keyHint && <div className={Styles.concert}>({keyHint})</div>}
        </div>
      </div>

      <h1 className={Styles.title}>{shownKey(tonic)} Major</h1>
      <p className={Styles.status}>{statusLine}</p>
      {!premium && (
        <p
          className={Styles.limit}
          style={{ visibility: phase === 'idle' ? 'visible' : 'hidden' }}
        >
          {runsLeft > 0 ? (
            <>
              {runsLeft} of {FREE_RUNS_PER_DAY} free runs left today.
            </>
          ) : (
            <>
              No free runs left today.
              <button
                type="button"
                className={Styles.limitBtn}
                onClick={() =>
                  openPricing(
                    `Free accounts get ${FREE_RUNS_PER_DAY} scale runs a day. Premium is unlimited.`,
                  )
                }
              >
                Go Premium
              </button>
            </>
          )}
        </p>
      )}

      <section className={Styles.stage}>
        <div className={Styles.staffBox}>
          <div className={phase === 'countin' ? Styles.staffDim : ''}>
            <ScaleNotes
              notes={displayedScale}
              instrument={selectedInstrument}
              bpm={bpm}
              activeIndex={activeIndex}
              statuses={statuses}
              fingerings={fingerings}
              stepMs={stepMs}
            />
          </div>
          {phase === 'countin' && countBeat !== null && (
            <div className={Styles.countIn} aria-live="assertive">
              <span key={countBeat} className={Styles.countNumber}>
                {countBeat}
              </span>
            </div>
          )}
        </div>

        <div className={Styles.infoRow}>
          <div className={Styles.sessionBar}>
            {/* always rendered so the layout never shifts; hidden until there is a score */}
            <span
              className={Styles.sessionStats}
              style={{ visibility: runJudged > 0 ? 'visible' : 'hidden' }}
            >
              {runRight}/{runJudged || 0} notes · {Math.round((runRight / (runJudged || 1)) * 100)}%
            </span>
          </div>
        </div>
      </section>

      <div className={`${Styles.instrumentWrap} instrument`}>
        <Instrument
          passive
          handleIncorrect={noop}
          handleAnswer={noop}
          instrument={selectedInstrument}
          handleLoading={handleLoading}
          mode={'Scales'}
        />
      </div>

      {showOptions && <Options toggleOptions={toggleOptions} page={'scales'} />}
    </main>
  );
};

export default Scales;